// Your wins (Accelerator.dc.html): the member's wins, newest first, each with its kind and date
// ("Marketing, 14 Sep"), and Add a win: what happened, 1 of the board's 5 kinds, and whether Adam may
// share it ("Happy for Adam to share this win?", design 4.5's share flag). A win has no amount
// (docs/data-model.md principle 9) and is dated today. Each win shows its "More about it" under its kind
// and date, kept whole with its line breaks, and Add a win and Edit have a box for it under the words
// (Adam's yes of 11 Oct, for the Accelerator's move: most of the old site's wins have one, and the copy
// keeps them). Each win the member logged has Edit (Adam's answer 6 of 7 Oct; no board draws it): its
// words, its kind, its more about it and the share tick, with Save the win, Cancel and Delete, which asks
// first, in the staff panel's words. 1 form is open at a time. A win
// staff logged for them stays as staff wrote it, with no Edit or Delete: only its share tick, "Happy
// for Adam to share this win?", which they change in place, because sharing is their own say (Adam's
// follow-up, 7 Oct; the database holds the same rule, migration 20261007120000). While staff view as
// a member nothing is added, changed or deleted. limit: the home shows the latest 3, and See all your wins opens the
// rest in place, each with its Edit, so a year of wins doesn't take over the home (the Accelerator critique's member
// 5, 20 Oct: wins are only changed and deleted here).
import { useEffect, useId, useRef, useState } from 'react'
import { readOnlyControl } from '../../lib/viewAs.jsx'
import { Tick } from '../../product/Icons.jsx'
import { COPY, SAVE_FAILED } from './copy.js'
import { WIN_DETAIL_MAX, winError } from './model.js'
import { addWin, deleteWin, saveShare, saveWin } from './data.js'
import { LIMITS } from './limits.js'

// The words, the more about it, the kind and the share tick, for a new win and for a changed 1
function WinFields({ title, setTitle, detail, setDetail, category, setCategory, share, setShare, onType }) {
  return (
    <>
      <input aria-label={COPY.winField} placeholder={COPY.winPlaceholder} value={title} maxLength={LIMITS.winTitle} autoFocus
        onChange={e => { setTitle(e.target.value); onType() }} />
      <textarea aria-label={COPY.winDetail} placeholder={COPY.winDetailPlaceholder} value={detail} maxLength={WIN_DETAIL_MAX} rows={3}
        onChange={e => { setDetail(e.target.value); onType() }} />
      <div className="acc-chips" role="group" aria-label={COPY.categoryLabel}>
        {COPY.categories.map(([key, name]) => (
          <button key={key} type="button" className={category === key ? 'acc-chip acc-chip--on' : 'acc-chip'}
            aria-pressed={category === key} onClick={() => setCategory(key)}>{name}</button>
        ))}
      </div>
      <label className="acc-share">
        <input type="checkbox" checked={share} onChange={e => setShare(e.target.checked)} />
        <span>{COPY.share}</span>
      </label>
    </>
  )
}

export function WinsCard({ wins, productId, personId, viewingAs, patch, limit = null }) {
  // null, 'new' (Add a win), or the id of the win being changed
  const [open, setOpen] = useState(null)
  // See all your wins pressed
  const [all, setAll] = useState(false)
  const shown = limit && !all ? wins.slice(0, limit) : wins
  const more = wins.length > shown.length
  const [said, setSaid] = useState(null)
  // where the keyboard goes back to once a form closes: the win's own Edit, or Add a win once it's gone
  const [focusTo, setFocusTo] = useState(null)
  const editButtons = useRef(new Map())
  const addButton = useRef(null)
  const adding = open === 'new'

  useEffect(() => {
    if (!focusTo) return
    const el = focusTo === 'add' ? addButton.current : editButtons.current.get(focusTo)
    if (el) el.focus()
    setFocusTo(null)
  }, [focusTo])

  function closeEdit(id, words) {
    setOpen(null)
    if (words) setSaid(words)
    setFocusTo(words === COPY.winDeleted ? 'add' : id)
  }

  function toggle() {
    if (viewingAs) return
    setOpen(o => (o === 'new' ? null : 'new'))
    setSaid(null)
  }

  function edit(id) {
    if (viewingAs) return
    setOpen(id)
    setSaid(null)
  }

  return (
    <section className="acc-card acc-wins" aria-labelledby="acc-wins-h">
      <div className="acc-wins-head">
        <h2 className="acc-h2" id="acc-wins-h">{COPY.wins}</h2>
        <button type="button" ref={addButton} className="acc-btn-line acc-btn--small" aria-expanded={adding} onClick={toggle} {...readOnlyControl(viewingAs)}>
          {adding ? COPY.cancel : COPY.addWin}
        </button>
      </div>
      {adding && (
        <AddWin productId={productId} personId={personId} viewingAs={viewingAs} patch={patch} onDone={() => setOpen(null)} />
      )}
      {wins.length === 0 && !adding && <p className="acc-text acc-text--small">{COPY.noWins}</p>}
      {wins.length > 0 && (
        <ul className="acc-win-list">
          {shown.map(w => (
            <li key={w.id} className={open === w.id ? 'acc-win acc-win--editing' : 'acc-win'}>
              {open === w.id
                ? (
                  <EditWin win={w} viewingAs={viewingAs} patch={patch}
                    onDone={words => closeEdit(w.id, words)} onCancel={() => closeEdit(w.id, null)} />
                )
                : w.memberLogged
                  ? (
                    <>
                      <span className="acc-win-tick" aria-hidden="true"><Tick size={15} /></span>
                      <span className="acc-win-text">
                        <span className="acc-win-title">{w.title}</span>
                        <span className="acc-win-meta">{w.meta}</span>
                        {w.detail && <span className="acc-win-detail">{w.detail}</span>}
                      </span>
                      <button type="button" className="acc-win-edit" aria-label={COPY.editLabel(w.title)}
                        ref={el => { if (el) editButtons.current.set(w.id, el); else editButtons.current.delete(w.id) }}
                        onClick={() => edit(w.id)} {...readOnlyControl(viewingAs)}>{COPY.edit}</button>
                    </>
                  )
                  : <StaffWin win={w} viewingAs={viewingAs} patch={patch} onSaved={() => setSaid(COPY.winSaved)} onChange={() => setSaid(null)} />}
            </li>
          ))}
        </ul>
      )}
      {more && (
        <button type="button" className="acc-link acc-wins-all" aria-expanded={false} onClick={() => setAll(true)}>{COPY.seeAllWins}</button>
      )}
      {said && <p className="acc-said" role="status">{said}</p>}
    </section>
  )
}

// A win staff logged for them: its words and kind as staff wrote them, and only the share tick, changed
// in place. A change saves at once ("Win saved."); one that fails goes back, with the shared line.
function StaffWin({ win, viewingAs, patch, onSaved, onChange }) {
  const titleId = useId()
  // the tick as pressed, before the database answers; back to the saved answer if it fails
  const [checked, setChecked] = useState(win.shareable)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  useEffect(() => { setChecked(win.shareable) }, [win.shareable])

  async function change(e) {
    const shareable = e.target.checked
    if (viewingAs || busy) return
    setChecked(shareable)
    setBusy(true)
    setLine(null)
    onChange()
    try {
      const row = await saveShare(win.id, shareable)
      patch(d => ({ ...d, wins: d.wins.map(w => (w.id === row.id ? row : w)) }))
      onSaved()
    } catch (err) {
      setChecked(win.shareable)
      setLine(winError(err) || SAVE_FAILED)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <span className="acc-win-tick" aria-hidden="true"><Tick size={15} /></span>
      <span className="acc-win-text">
        <span className="acc-win-title" id={titleId}>{win.title}</span>
        <span className="acc-win-meta">{win.meta}</span>
        {win.detail && <span className="acc-win-detail">{win.detail}</span>}
        <label className="acc-share acc-win-share">
          <input type="checkbox" checked={checked} onChange={change} aria-describedby={titleId}
            aria-busy={busy || undefined} {...readOnlyControl(viewingAs)} />
          <span>{COPY.share}</span>
        </label>
        {line && <span className="acc-msg" role="alert">{line}</span>}
      </span>
    </>
  )
}

// Add a win, as the board draws it
function AddWin({ productId, personId, viewingAs, patch, onDone }) {
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')
  const [category, setCategory] = useState('deal')
  const [share, setShare] = useState(false)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)

  async function save(e) {
    e.preventDefault()
    if (viewingAs || busy) return
    const t = title.trim()
    if (!t || t.length > LIMITS.winTitle) { setLine(COPY.winTitle); return }
    const d = detail.trim()
    if (d.length > WIN_DETAIL_MAX) { setLine(COPY.winDetailLength(WIN_DETAIL_MAX)); return }
    setBusy(true)
    setLine(null)
    try {
      const row = await addWin({ personId, productId, title: t, category, detail: d, shareable: share })
      patch(d => ({ ...d, wins: [row, ...d.wins.filter(w => w.id !== row.id)] }))
      onDone()
    } catch (err) {
      setLine(winError(err) || SAVE_FAILED)
      setBusy(false)
    }
  }

  return (
    <form className="acc-win-form" onSubmit={save} noValidate>
      <WinFields title={title} setTitle={setTitle} detail={detail} setDetail={setDetail} category={category} setCategory={setCategory}
        share={share} setShare={setShare} onType={() => setLine(null)} />
      <button type="submit" className="acc-btn acc-btn--small" aria-busy={busy || undefined}>{COPY.saveWin}</button>
      {line && <p className="acc-msg" role="alert">{line}</p>}
    </form>
  )
}

// Edit on 1 of the wins they logged: its words, its more about it, its kind and the share tick; Save the
// win, Cancel, and Delete, which asks first
function EditWin({ win, viewingAs, patch, onDone, onCancel }) {
  const [title, setTitle] = useState(win.title)
  const [detail, setDetail] = useState(win.detail || '')
  const [category, setCategory] = useState(win.category)
  const [share, setShare] = useState(Boolean(win.shareable))
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [line, setLine] = useState(null)

  async function save(e) {
    e.preventDefault()
    if (viewingAs || busy) return
    const t = title.trim()
    if (!t || t.length > LIMITS.winTitle) { setLine(COPY.winTitle); return }
    const d = detail.trim()
    if (d.length > WIN_DETAIL_MAX) { setLine(COPY.winDetailLength(WIN_DETAIL_MAX)); return }
    setBusy(true)
    setLine(null)
    try {
      const row = await saveWin({ winId: win.id, title: t, category, detail: d, shareable: share })
      patch(d => ({ ...d, wins: d.wins.map(w => (w.id === row.id ? row : w)) }))
      onDone(COPY.winSaved)
    } catch (err) {
      setLine(winError(err) || SAVE_FAILED)
      setBusy(false)
    }
  }

  async function remove() {
    if (viewingAs || busy) return
    setBusy(true)
    setLine(null)
    try {
      await deleteWin(win.id)
      patch(d => ({ ...d, wins: d.wins.filter(w => w.id !== win.id) }))
      onDone(COPY.winDeleted)
    } catch {
      setLine(COPY.deleteFailed)
      setConfirming(false)
      setBusy(false)
    }
  }

  return (
    <form className="acc-win-form acc-win-form--edit" onSubmit={save} noValidate aria-label={COPY.editLabel(win.title)}>
      <WinFields title={title} setTitle={setTitle} detail={detail} setDetail={setDetail} category={category} setCategory={setCategory}
        share={share} setShare={setShare} onType={() => setLine(null)} />
      {confirming
        ? (
          <div className="acc-win-confirm">
            <p className="acc-msg">{COPY.deleteConfirm(win.title)}</p>
            <div className="acc-row">
              <button type="button" className="acc-btn acc-btn--small" aria-busy={busy || undefined} onClick={remove}
                {...readOnlyControl(viewingAs)}>{COPY.deleteYes}</button>
              <button type="button" className="acc-btn-line acc-btn--small" onClick={() => setConfirming(false)}>{COPY.keep}</button>
            </div>
          </div>
        )
        : (
          <div className="acc-row">
            <button type="submit" className="acc-btn acc-btn--small" aria-busy={busy || undefined} {...readOnlyControl(viewingAs)}>{COPY.saveWin}</button>
            <button type="button" className="acc-btn-line acc-btn--small" onClick={onCancel}>{COPY.cancel}</button>
            <button type="button" className="acc-btn-line acc-btn--small acc-win-delete"
              onClick={() => { setConfirming(true); setLine(null) }} {...readOnlyControl(viewingAs)}>{COPY.deleteWin}</button>
          </div>
        )}
      {line && <p className="acc-msg" role="alert">{line}</p>}
    </form>
  )
}
